import { Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import L from 'leaflet';
import HeatmapOverlay from 'leaflet-heatmap';
import 'node_modules/leaflet-timedimension/dist/leaflet.timedimension.src.js';
import { ModuleManager } from 'src/shared/models/module-manager';
import { ModuleManagerRefreshType } from 'src/shared/models/module-manager/module-manager-options.model';
import { LocationInfo, MapData } from './heatmap.model';
import { TimeDimension as TimeDimension, TimeDimensionControlOptions, TimeDimensionOptions } from './heatmap.model.d';
import { HeatmapService } from './heatmap.service';

//Declared here to define leaflet, a hacky way to get the library to work with Angular
declare module 'leaflet' {
    let TimeDimension: any;
    export interface MapOptions {
        timeDimension?: boolean | TimeDimension;
        timeDimensionControl?: boolean;
        timeDimensionControlOptions?: TimeDimensionControlOptions;
        timeDimensionOptions?: TimeDimensionOptions;
    }
}

@Component({
    selector: 'cv-heatmap',
    templateUrl: './heatmap.component.html',
    styleUrls: ['./heatmap.component.scss']
})

export class HeatmapComponent implements OnInit {

    @Input() moduleManager: ModuleManager;
    @Input() moduleKey: string;

    @ViewChild('heatmapContainer', { read: ElementRef }) mapElement: ElementRef<HTMLDivElement>;

    mapData: MapData[] = [];
    dataLoading: boolean = false;
    geoDic: Map<Date,Map<string, number>>; 
    heatMap; //uses a type from leafletjs
    isFirstLoad: boolean = true;
    locationInfo: LocationInfo = null;

    constructor(
        private service: HeatmapService,
    ) { }

    ngOnInit(): void {
        this.initializeComponentAsync();
    }

    async initializeComponentAsync() {
        this.moduleManager.setOptions({
            onSettingsApplied: (settings) => {
                this.loadData(false);
            },
            refreshType: ModuleManagerRefreshType.Minute,
            hasRefresh: true,
            onRefresh: (isAutoRefresh) => {
                return this.loadData(isAutoRefresh);
            },
            canRefresh: () => {
                return true;
            }
        });

        //#region States and Settings
        this.moduleManager.addState({
            name: 'savedLocationInfo',
            getStateData: () => {
                return JSON.stringify(this.locationInfo)
            },
            onStateLoaded: (stateData) => {
                if (!stateData) { // No table state loaded
                    this.locationInfo = {
                        Lat: 40,
                        Lng: -110,
                        Zoom: 2
                    }
                    return;
                }
                this.locationInfo = JSON.parse(stateData);
            },
            autoLoad: true
        });
    }

    // This function returns the string value and object of the Date d argument, truncated to the minute value
    dateTruncToMins(d) {
        return {
            dateObject: new Date(d.getFullYear(), d.getMonth(), d.getDate(), d.getHours(), d.getMinutes()),
            // 2020-01-01T09:30
            dateString: d.toString("yyyy-MM-ddTHH:mm"),
        };
    }

    async loadData(isAutoRefresh) {
        //Used to get the different context of 'this' so packages can play nice with Angular
        let context = this;

        let HeatMap = L.TimeDimension.Layer.extend({
            initialize: function (options) {
                context.mapData = [];
                for (let timeIndex in context.geoDic) {
                    let bucket = context.geoDic[timeIndex];
                    for (let rawGeo in bucket) {
                        let geoInfo = rawGeo.split("|");
                        context.mapData.push({
                            lat: geoInfo[0],
                            lng: geoInfo[1],
                            count: bucket[rawGeo],
                            timestamp: timeIndex
                        });
                    }
                }

                let heatmapCfg = context.getHeatmapOptions(options.heatmatOptions || {});
                let layer = new HeatmapOverlay(heatmapCfg);
                L.TimeDimension.Layer.prototype.initialize.call(this, layer, options);
                this._currentLoadedTime = 0;
                this._currentTimeData = {
                    max: this.options.heatmapMax || 2000,
                    data: []
                };
                this._baseURL = this.options.baseURL || null;
                this._period = this.options.period || "PT5M";
            },

            getHeatmapOptions: (options) => {
                let config = {};
                let defaultConfig = {
                    radius: 2,
                    maxOpacity: .8,
                    scaleRadius: true,
                    useLocalExtrema: true,
                    latField: 'lat',
                    lngField: 'lng',
                    valueField: 'count',
                };
                for (let attrname in defaultConfig) {
                    config[attrname] = defaultConfig[attrname];
                }
                for (let attrname in options) {
                    config[attrname] = options[attrname];
                }
                return config;
            },

            onAdd: function (map) {
                L.TimeDimension.Layer.prototype.onAdd.call(this, map);
                map.addLayer(this._baseLayer);
                if (this._timeDimension) {
                    this._getDataForTime(this._timeDimension.getCurrentTime());
                }
            },

            _onNewTimeLoading: function (ev) {
                this._getDataForTime(ev.time);
                return;
            },

            isReady: function (time) {
                return (this._currentLoadedTime == time);
            },

            _update: function () {
                this._currentTimeData.data = [];
                let currentLoadedDateTime = new Date(this._currentLoadedTime);
                for (let i = 0; i < context.mapData.length; i++) {
                    let el = context.mapData[i];
                    let tmp = new Date(el.timestamp);

                    let adjustedLoadedDT = new Date(currentLoadedDateTime);
                    let fiveMinsAfter = new Date(adjustedLoadedDT.getTime() + 5 * 60 * 1000);

                    if (adjustedLoadedDT <= tmp && fiveMinsAfter >= tmp) {
                        this._currentTimeData.data.push(el);
                    }
                }

                this._baseLayer.setData(this._currentTimeData);
                return true;
            },

            _getDataForTime: function (time) {
                if (!this._baseURL || !this._map) {
                    return;
                }

                this._currentLoadedTime = time;
                if (this._timeDimension && time == this._timeDimension.getCurrentTime() && !this._timeDimension.isLoading()) {
                    this._update();
                }

                this.fire('timeload', {
                    time: time
                });
            },

            _constructQuery: function (time) {
                let endDate = new Date(Date.now());
                L.TimeDimension.util.addTimeDuration(endDate, this._period, false);

                this._baseURL = "../api/v2/modules/GetGeoTagData/";
                let params = context.moduleKey;

                let url = this._baseURL + params;
                return url;
            }
        });

        this.heatMap = function (options) {
            return new HeatMap(options);
        };

        let response = await this.service.LoadData(this.moduleKey, isAutoRefresh, this.isFirstLoad);
        if (response) {
            this.isFirstLoad = false;
            this.geoDic = response.GeoDic;

            let buckets = Object.keys(this.geoDic);

            let timeBarStart = buckets[0];
            let timeBarEnd = buckets[buckets.length - 1];

            let twohoursAgo = this.dateTruncToMins(new Date(timeBarStart));
            let currentTime = this.dateTruncToMins(new Date(timeBarEnd));

            let currentTimeMS = currentTime.dateObject.getTime();

            this.mapElement.nativeElement.innerHTML = "";
            const geoMap = document.createElement("div");
            geoMap.id = 'heatmap-' + this.moduleKey;
            geoMap.className = 'map';
            geoMap.style.width = '100%';
            geoMap.style.height = '100%';
            this.mapElement.nativeElement.appendChild(geoMap);

            let map = null;

            if (this.locationInfo && this.locationInfo.Zoom && this.locationInfo.Lat && this.locationInfo.Lng) {
                map = L.map(geoMap.id, {
                    zoom: this.locationInfo.Zoom,
                    timeDimension: true,
                    timeDimensionOptions: {
                        timeInterval: twohoursAgo.dateString + "/" + currentTime.dateString,
                        period: "PT5M",
                        currentTime: currentTimeMS
                    },
                    maxBoundsViscosity: 1.0,
                    zoomDelta: 0.5,
                    zoomSnap: 0.5,
                    dragging: true,
                    center: [this.locationInfo.Lat, this.locationInfo.Lng],
                    attributionControl: false,
                }).setView([this.locationInfo.Lat, this.locationInfo.Lng], this.locationInfo.Zoom);
            } else {
                map = L.map(geoMap.id, {
                    zoom: 2,
                    timeDimension: true,
                    timeDimensionOptions: {
                        timeInterval: twohoursAgo.dateString + "/" + currentTime.dateString,
                        period: "PT5M",
                        currentTime: currentTimeMS
                    },
                    maxBoundsViscosity: 1.0,
                    zoomDelta: 0.5,
                    zoomSnap: 0.5,
                    dragging: true,
                    center: [40, -100],
                    attributionControl: false,
                }).setView([40, -110], 2);
            }

            map.setMaxBounds([[-90, -160], [90, 200]]);

            L.control.scale().addTo(map);

            let layer = L.tileLayer(
                'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 16,
                minZoom: 1,
            }
            );
            map.addLayer(layer);

            let heatmap = this.heatMap({
                baseURL: '../api',
            });
            heatmap.addTo(map);

            L.Control['TimeDimensionCustom'] = L.Control['TimeDimension'].extend({
                _getDisplayDateFormat: (date) => {
                    return date.toLocaleTimeString();
                },
                _getDisplaySpeed: function (fps) {
                    return fps;
                },
            });

            map.addControl(new L.Control['TimeDimensionCustom']({
                playerOptions: {
                    buffer: 1,
                    minBufferReady: -1,
                    loop: true,
                    transitionTime: 333
                }
            }));

            map.on('moveend zoomend', (e) => this.saveLocationInfo(map));

            this.moduleManager.setAutoRefreshReady();

            setInterval(() => {
                if (map.getSize().x > 1000) {
                    map.setMinZoom(2.5);
                } else if (map.getSize().x > 800) {
                    map.setMinZoom(2);
                } else if (map.getSize().x > 600) {
                    map.setMinZoom(1.5);
                } else {
                    map.setMinZoom(1);
                }
                map.invalidateSize()
            }, 1000);

            map.timeDimension.setCurrentTime(currentTime);
            if (map.timeDimension.pause) {
                map.timeDimension.pause();
            }
        }
    }

    saveLocationInfo(map) {
        this.locationInfo = {
            Lat: map.getCenter().lat,
            Lng: map.getCenter().lng,
            Zoom: map.getZoom()
        };
        this.moduleManager.saveState();
    }

    getHeatmapOptions(options) {
        let config = {};
        let defaultConfig = {
            radius: 2,
            maxOpacity: .8,
            scaleRadius: true,
            useLocalExtrema: true,
            latField: 'lat',
            lngField: 'lng',
            valueField: 'count',
        };
        for (let attrname in defaultConfig) {
            config[attrname] = defaultConfig[attrname];
        }
        for (let attrname in options) {
            config[attrname] = options[attrname];
        }
        return config;
    }
}
